import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  MoreHorizontal, 
  Tag, 
  AlertTriangle, 
  Clock, 
  UserPlus, 
  Scale, 
  CheckCircle,
  Loader2,
  Plus,
  Trash2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useSupportInbox, DEMAND_TYPE_CONFIG, PRIORITY_CONFIG } from '@/hooks/useSupportInbox';
import { useSessionEscalations, RESOLUTION_TYPE_CONFIG } from '@/hooks/useSessionEscalations';
import { useCreateSiblingSession } from '@/hooks/useCreateSiblingSession';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useDepartments } from '@/hooks/useDepartments';

// Customer Service department ID - pre-selected for transfers
const CS_DEPARTMENT_ID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';
import { EscalateModal } from './EscalateModal';
import type { SessionWithSla, DemandType, ResolutionType } from '@/types/support.types';

interface SessionActionsProps {
  session: SessionWithSla;
  existingDemandTypes?: DemandType[];
  onSessionUpdated?: () => void;
  onNewSessionCreated?: (sessionId: string) => void;
}

export function SessionActions({ 
  session, 
  existingDemandTypes = [], 
  onSessionUpdated,
  onNewSessionCreated 
}: SessionActionsProps) {
  const queryClient = useQueryClient();
  const { data: currentAppUser } = useCurrentAppUser();
  const { users: appUsers } = useAppUsers();
  const { departments } = useDepartments();
  const { 
    changePriority, 
    changeStatus, 
    resolveSession, 
    assignAgent,
    isChangingPriority,
    isChangingStatus,
    isResolving,
    isAssigning,
  } = useSupportInbox();
  const { escalations, createEscalation, isCreating } = useSessionEscalations(session.id);
  const hasPendingEscalation = escalations.some(e => e.status === 'pending');
  const { createSiblingSession, isCreating: isCreatingSession } = useCreateSiblingSession();

  // Modal states
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [escalateModalOpen, setEscalateModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);

  // Form states
  const [transferDepartmentId, setTransferDepartmentId] = useState(CS_DEPARTMENT_ID);
  const [transferAgentId, setTransferAgentId] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [resolutionType, setResolutionType] = useState<ResolutionType>('resolved');
  const [resolutionNotes, setResolutionNotes] = useState('');

  // Admin check — uses system_role from authenticated query, not client-side storage
  const isAdmin = 
    currentAppUser?.system_role?.code === 'admin' || 
    currentAppUser?.system_role?.code === 'it';

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    const { error } = await supabase
      .from('support_sessions')
      .delete()
      .eq('id', session.id!);
    setIsDeleting(false);
    if (error) {
      toast.error('Erro ao excluir chamado');
      return;
    }
    toast.success('Chamado excluído');
    setDeleteDialogOpen(false);
    onSessionUpdated?.();
    queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
  };

  // Helper function for agent name
  const getUserName = (user: typeof appUsers[0]) => {
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Sem nome';
  };

  // Filter agents by selected department (excluding current assignee and self)
  const filteredAgents = appUsers
    .filter(u => 
      u.active && 
      u.department_id === transferDepartmentId &&
      u.id !== session.assigned_to
    )
    .sort((a, b) => getUserName(a).localeCompare(getUserName(b)));

  const handleClassify = async (demandType: DemandType) => {
    try {
      // Update demand_type via direct supabase call
      const { error } = await supabase
        .from('support_sessions')
        .update({ demand_type: demandType })
        .eq('id', session.id);
      
      if (error) {
        // Check for unique constraint violation
        if (error.code === '23505' || error.message?.includes('uq_active_session_per_contact_demand')) {
          const typeLabel = DEMAND_TYPE_CONFIG[demandType]?.label || demandType;
          toast.error(`Já existe um chamado ativo de ${typeLabel} para este contato`);
          return;
        }
        throw error;
      }
      toast.success(`Classificado como ${DEMAND_TYPE_CONFIG[demandType].label}`);
      onSessionUpdated?.();
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    } catch (err) {
      toast.error('Erro ao classificar chamado');
    }
  };

  const handleCreateSiblingSession = async (demandType: DemandType) => {
    if (!session.contact_id) {
      toast.error('Contato não encontrado');
      return;
    }

    try {
      const newSessionId = await createSiblingSession({
        contactId: session.contact_id,
        demandType,
        assignedTo: currentAppUser?.id,
        priority: 'normal',
        queue: 'general',
      });
      onNewSessionCreated?.(newSessionId);
    } catch (err) {
      // Error already handled in hook
    }
  };

  const handlePriority = async (priority: string) => {
    try {
      await changePriority({ session_id: session.id!, priority });
      toast.success(`Prioridade alterada para ${PRIORITY_CONFIG[priority as keyof typeof PRIORITY_CONFIG]?.label || priority}`);
      onSessionUpdated?.();
    } catch (err) {
      toast.error('Erro ao alterar prioridade');
    }
  };

  const handleStatus = async (status: 'waiting_client' | 'waiting_internal') => {
    try {
      await changeStatus({ session_id: session.id!, status });
      toast.success(status === 'waiting_client' ? 'Aguardando cliente' : 'Aguardando interno');
      onSessionUpdated?.();
    } catch (err) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleTransfer = async () => {
    if (!transferAgentId || !transferNote.trim()) {
      toast.error('Selecione um agente e adicione uma nota');
      return;
    }

    try {
      await assignAgent({ session_id: session.id!, agent_id: transferAgentId });
      
      // Add transfer note as session note (if table exists)
      // For now, just show success
      toast.success('Chamado transferido com sucesso');
      // Reset modal state
      setTransferModalOpen(false);
      setTransferDepartmentId(CS_DEPARTMENT_ID);
      setTransferAgentId('');
      setTransferNote('');
      onSessionUpdated?.();
    } catch (err) {
      toast.error('Erro ao transferir chamado');
    }
  };


  const handleResolve = async () => {
    if (hasPendingEscalation) return;
    try {
      await resolveSession({
        session_id: session.id!,
        resolution_type: resolutionType,
        resolution_notes: resolutionNotes || undefined,
      });
      toast.success('Chamado resolvido');
      setResolveModalOpen(false);
      setResolutionType('resolved');
      setResolutionNotes('');
      onSessionUpdated?.();
    } catch (err) {
      toast.error('Erro ao resolver chamado');
    }
  };


  const isLoading = isChangingPriority || isChangingStatus || isResolving || isAssigning || isCreating || isCreatingSession;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8" disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <MoreHorizontal className="h-4 w-4 mr-1" />
                Ações
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 max-h-[80vh] overflow-y-auto">
          {/* Classify */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Tag className="h-4 w-4 mr-2" />
              Classificar
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {(Object.entries(DEMAND_TYPE_CONFIG) as [DemandType, { label: string }][]).map(([type, config]) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => handleClassify(type)}
                  className={session.demand_type === type ? 'bg-accent' : ''}
                >
                  {config.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Priority */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <AlertTriangle className="h-4 w-4 mr-2" />
              Prioridade
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {Object.entries(PRIORITY_CONFIG).map(([priority, config]) => (
                <DropdownMenuItem
                  key={priority}
                  onClick={() => handlePriority(priority)}
                  className={session.priority === priority ? 'bg-accent' : ''}
                >
                  {config.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />

          {/* New Session */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={isCreatingSession}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Chamado
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {(Object.entries(DEMAND_TYPE_CONFIG) as [DemandType, { label: string }][]).map(([type, config]) => {
                const isExisting = existingDemandTypes.includes(type);
                return (
                  <DropdownMenuItem
                    key={type}
                    onClick={() => handleCreateSiblingSession(type)}
                    disabled={isExisting}
                    className={isExisting ? 'opacity-50' : ''}
                  >
                    {config.label}
                    {isExisting && <span className="ml-2 text-xs text-muted-foreground">(ativa)</span>}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />

          {/* Status */}
          <DropdownMenuItem onClick={() => handleStatus('waiting_client')}>
            <Clock className="h-4 w-4 mr-2" />
            Aguardando Cliente
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleStatus('waiting_internal')}>
            <Clock className="h-4 w-4 mr-2" />
            Aguardando Interno
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Transfer */}
          <DropdownMenuItem onClick={() => setTransferModalOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Transferir...
          </DropdownMenuItem>

          {/* Escalate */}
          <DropdownMenuItem onClick={() => setEscalateModalOpen(true)}>
            <Scale className="h-4 w-4 mr-2" />
            Escalar...
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Resolve */}
          <DropdownMenuItem 
            onClick={() => !hasPendingEscalation && setResolveModalOpen(true)}
            disabled={hasPendingEscalation}
            className={hasPendingEscalation ? 'text-muted-foreground' : ''}
          >
            {hasPendingEscalation ? (
              <>
                <AlertTriangle className="h-4 w-4 mr-2 text-warning" />
                Resolver... (escalação pendente)
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Resolver...
              </>
            )}
          </DropdownMenuItem>

          {/* Delete — admin/it only */}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Chamado
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Transfer Modal */}
      <Dialog open={transferModalOpen} onOpenChange={(open) => {
        if (!open) {
          setTransferDepartmentId(CS_DEPARTMENT_ID);
          setTransferAgentId('');
          setTransferNote('');
        }
        setTransferModalOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir Chamado</DialogTitle>
            <DialogDescription>
              Transfira este chamado para outro agente de atendimento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Departamento</Label>
              <Select 
                value={transferDepartmentId} 
                onValueChange={(v) => {
                  setTransferDepartmentId(v);
                  setTransferAgentId('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o departamento" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Agente</Label>
              <Select value={transferAgentId} onValueChange={setTransferAgentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um agente" />
                </SelectTrigger>
                <SelectContent>
                  {filteredAgents.length === 0 ? (
                    <SelectItem value="none" disabled>
                      Nenhum agente disponível
                    </SelectItem>
                  ) : (
                    filteredAgents.map(agent => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {getUserName(agent)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nota de transferência *</Label>
              <Textarea
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder="Descreva o motivo da transferência..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleTransfer} disabled={isAssigning || !transferAgentId || !transferNote.trim()}>
              {isAssigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate Modal */}
      <EscalateModal
        sessionId={session.id!}
        open={escalateModalOpen}
        onOpenChange={setEscalateModalOpen}
        onSuccess={onSessionUpdated}
        defaultPriority={session.priority || 'normal'}
      />

      {/* Resolve Modal */}
      <Dialog open={resolveModalOpen} onOpenChange={setResolveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolver Chamado</DialogTitle>
            <DialogDescription>
              Marque este chamado como resolvido.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Resolução</Label>
              <Select value={resolutionType} onValueChange={(v) => setResolutionType(v as ResolutionType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(RESOLUTION_TYPE_CONFIG) as [ResolutionType, { label: string }][]).map(([type, config]) => (
                    <SelectItem key={type} value={type}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Adicione notas sobre a resolução..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleResolve} disabled={isResolving}>
              {isResolving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Resolver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Chamado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. O chamado e todas as suas interações 
              serão permanentemente excluídos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
